import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Film, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

function VideoConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/video-access/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/video-access/config", cfg);
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Video settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-6 kr-card p-5" data-testid="video-config-card">
      <div className="font-display text-lg">Generation Settings</div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <div className="kr-overline mb-2">ENABLED RATIOS</div>
          <div className="flex flex-wrap gap-1.5">
            {cfg.all_ratios.map((r) => (
              <button key={r} onClick={() => setCfg((c) => ({ ...c, enabled_ratios: toggle(c.enabled_ratios, r) }))}
                className={`px-3 py-1.5 rounded-full text-xs font-mono border ${cfg.enabled_ratios.includes(r) ? "text-black font-bold" : "text-white/45"}`}
                style={{ background: cfg.enabled_ratios.includes(r) ? "#FF8A22" : "transparent", borderColor: cfg.enabled_ratios.includes(r) ? "#FF8A22" : "rgba(255,255,255,0.12)" }}
                data-testid={`vc-ratio-${r.replace(":", "x")}`}>{r}</button>
            ))}
          </div>
          <div className="kr-overline mt-3 mb-1.5">DEFAULT RATIO</div>
          <select value={cfg.default_ratio} onChange={(e) => setCfg((c) => ({ ...c, default_ratio: e.target.value }))}
            className="bg-[#07080B] border border-white/10 rounded-md h-9 px-2 text-sm w-full" data-testid="vc-default-ratio">
            {cfg.enabled_ratios.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <div className="kr-overline mb-2">ENABLED QUALITIES</div>
          <div className="flex flex-wrap gap-1.5">
            {cfg.all_qualities.map((q) => (
              <button key={q} onClick={() => setCfg((c) => ({ ...c, enabled_qualities: toggle(c.enabled_qualities, q) }))}
                className={`px-3 py-1.5 rounded-full text-xs font-mono border capitalize ${cfg.enabled_qualities.includes(q) ? "text-black font-bold" : "text-white/45"}`}
                style={{ background: cfg.enabled_qualities.includes(q) ? "#FF8A22" : "transparent", borderColor: cfg.enabled_qualities.includes(q) ? "#FF8A22" : "rgba(255,255,255,0.12)" }}
                data-testid={`vc-quality-${q}`}>{q}</button>
            ))}
          </div>
          <div className="kr-overline mt-3 mb-1.5">DEFAULT QUALITY</div>
          <select value={cfg.default_quality} onChange={(e) => setCfg((c) => ({ ...c, default_quality: e.target.value }))}
            className="bg-[#07080B] border border-white/10 rounded-md h-9 px-2 text-sm w-full capitalize" data-testid="vc-default-quality">
            {cfg.enabled_qualities.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
        <div>
          <div className="kr-overline mb-2">MAX DURATION (S)</div>
          <select value={cfg.max_duration} onChange={(e) => setCfg((c) => ({ ...c, max_duration: Number(e.target.value) }))}
            className="bg-[#07080B] border border-white/10 rounded-md h-9 px-2 text-sm w-full" data-testid="vc-max-duration">
            {[4, 8, 12].map((d) => <option key={d} value={d}>{d}s</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-white/70 mt-4">
            <input type="checkbox" checked={!!cfg.upscale_enabled} onChange={(e) => setCfg((c) => ({ ...c, upscale_enabled: e.target.checked }))} data-testid="vc-upscale" />
            AI upscaling (Best Quality)
          </label>
        </div>
        <div className="flex flex-col justify-between">
          <div className="text-xs text-white/40">Credit costs per duration and quality tier are configured in module pricing (video.generate.*, video.quality.*).</div>
          <Button onClick={save} disabled={saving} className="h-10 text-black font-semibold border-0 mt-3" style={{ background: "#FF8A22" }} data-testid="vc-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminVideoAccess() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/video-access", { params: q ? { q } : {} }).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleSel = (uid) => setSel((s) => (s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]));

  const change = async (action, user_ids) => {
    if (user_ids.length === 0) return toast.error("Select at least one user");
    setBusy(true);
    try {
      await api.post(`/admin/video-access/${action}`, { user_ids, expires_at: expires ? new Date(expires).toISOString() : null, note: note || null });
      toast.success(`${action === "grant" ? "Granted" : "Revoked"} for ${user_ids.length} user(s)`);
      setSel([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1300px] mx-auto">
      <div className="kr-overline" style={{ color: "#FF8A22" }}>[ VIDEO GENERATOR ACCESS ]</div>
      <h1 className="font-display text-3xl mt-2 tracking-tight flex items-center gap-3"><Film className="h-7 w-7" style={{ color: "#FF8A22" }} /> Video Access Control</h1>
      <p className="text-white/50 text-sm mt-1.5">The Video Generator is restricted — only users granted here can see and use it. Every change is audit-logged.</p>

      <VideoConfigCard />

      <div className="mt-6 kr-card p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="kr-overline mb-1.5">SEARCH USERS</div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="email or name…" className="bg-[#07080B] border-white/10 h-10" data-testid="va-search" />
        </div>
        <div>
          <div className="kr-overline mb-1.5">EXPIRES (OPTIONAL)</div>
          <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="bg-[#07080B] border-white/10 h-10 w-40" data-testid="va-expires" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="kr-overline mb-1.5">INTERNAL NOTE</div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. beta tester" className="bg-[#07080B] border-white/10 h-10" data-testid="va-note" />
        </div>
        <Button disabled={busy} onClick={() => change("grant", sel)} className="h-10 text-black font-semibold border-0" style={{ background: "#00DC82" }} data-testid="va-bulk-grant">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Grant ({sel.length})</>}
        </Button>
        <Button disabled={busy} onClick={() => change("revoke", sel)} variant="outline" className="h-10 border-red-400/30 text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="va-bulk-revoke">
          Revoke ({sel.length})
        </Button>
      </div>

      <div className="mt-4 kr-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 border-b border-white/5">
              <th className="p-3 w-8"></th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">User</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Access</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Expires</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Last use</th>
              <th className="text-right p-3 font-mono text-[10px] tracking-wider uppercase">Videos</th>
              <th className="text-right p-3 font-mono text-[10px] tracking-wider uppercase">Credits</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Note</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-b border-white/5" data-testid={`va-row-${u.user_id}`}>
                <td className="p-3"><input type="checkbox" checked={sel.includes(u.user_id)} onChange={() => toggleSel(u.user_id)} data-testid={`va-check-${u.user_id}`} /></td>
                <td className="p-3">
                  <div className="text-white/85">{u.name}</div>
                  <div className="text-xs text-white/40">{u.email} · {u.plan}{u.role === "super_admin" ? " · SUPER" : ""}</div>
                </td>
                <td className="p-3">
                  {u.role === "super_admin" || u.access_active
                    ? <span className="flex items-center gap-1.5 text-[#00DC82] text-xs"><Check className="h-3.5 w-3.5" /> Active</span>
                    : <span className="flex items-center gap-1.5 text-white/35 text-xs"><X className="h-3.5 w-3.5" /> No access</span>}
                </td>
                <td className="p-3 text-xs text-white/50">{u.expires_at ? new Date(u.expires_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-xs text-white/50">{u.last_use ? new Date(u.last_use).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right font-mono text-xs">{u.total_generations}</td>
                <td className="p-3 text-right font-mono text-xs">{u.credits_consumed}</td>
                <td className="p-3 text-xs text-white/45 max-w-[140px] truncate">{u.note || "—"}</td>
                <td className="p-3">
                  {u.role !== "super_admin" && (u.access_active
                    ? <Button size="sm" variant="outline" onClick={() => change("revoke", [u.user_id])} className="h-7 text-[11px] border-red-400/25 text-red-300 hover:bg-red-500/10" data-testid={`va-revoke-${u.user_id}`}>Revoke</Button>
                    : <Button size="sm" onClick={() => change("grant", [u.user_id])} className="h-7 text-[11px] text-black border-0" style={{ background: "#00DC82" }} data-testid={`va-grant-${u.user_id}`}>Grant</Button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
